// FILE: apps/admin/src/pages/Users.tsx
// VERSION: 2.3.0
// START_MODULE_CONTRACT
//   PURPOSE: Admin Users directory with mobile-cards layout, sortable columns, completed orders count, and User Detail Drawer with platform revenue/LTV breakdown, personal & spend-tier commission controls, potentially linked accounts detection, custom profile moderation, referral stats, and IP-GEO telemetry.
//   SCOPE: USERS_MOBILE_SORT_OPTIONS, parseUsersSortOption, Users component, and UserDetailDrawer slide-over panel.
//   DEPENDS: ../api.js, ../audit-catalog.js, ../components/ConfirmDialog.js, ../components/Icons.js, ../components/UserAvatar.js, ../display-currency.js, ../toast.js
//   LINKS: M-ADMIN
//   ROLE: RUNTIME
//   MAP_MODE: EXPORTS
// END_MODULE_CONTRACT
//
// START_MODULE_MAP
//   USERS_MOBILE_SORT_OPTIONS - Sort option catalog for mobile/desktop Users toolbar select
//   Users - Admin Users directory with mobile cards, sorting, completed orders column, LTV card, personal commission controls, and linked accounts explorer
//   parseUsersSortOption - Pure parser converting a combined "sortBy:sortDir" option string into validated Users sort state
// END_MODULE_MAP

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { api, dt, gram, num, rub } from '../api.js';
import { describeAuditAction } from '../audit-catalog.js';
import { ConfirmDialog } from '../components/ConfirmDialog.js';
import { AdminIcon, CompanionIcon } from '../components/Icons.js';
import { resolveAdminUserDisplayName, UserAvatar } from '../components/UserAvatar.js';
import { useDisplayCurrency } from '../display-currency.js';
import { useToast } from '../toast.js';

type PlatformRole = 'superadministrator' | 'administrator' | 'support';

interface UserTransactionRow {
  id: string;
  currency: 'RUB' | 'GRAM';
  type: string;
  amountKopecks: number;
  balanceAfterKopecks: number;
  description: string;
  createdAt: string;
}

interface UserDeviceRow {
  id: string;
  deviceFingerprint: string;
  platform: string | null;
  telegramClient?: string | null;
  telegramVersion?: string | null;
  osName?: string | null;
  deviceModel?: string | null;
  browserName?: string | null;
  languageCode?: string | null;
  screenResolution?: string | null;
  timezone?: string | null;
  userAgent?: string | null;
  ipAddress: string | null;
  countryCode?: string | null;
  countryName?: string | null;
  regionName?: string | null;
  cityName?: string | null;
  ispName?: string | null;
  firstSeenAt?: string;
  lastSeenAt: string;
}

interface UserLoginEventRow {
  id: string;
  source: string;
  ipAddress: string | null;
  telegramClient: string | null;
  telegramVersion: string | null;
  osName: string | null;
  deviceModel: string | null;
  browserName: string | null;
  userAgent: string | null;
  languageCode: string | null;
  timezone: string | null;
  screenResolution: string | null;
  isTelegramPremium: boolean;
  countryCode: string | null;
  countryName: string | null;
  regionName: string | null;
  cityName: string | null;
  ispName: string | null;
  createdAt: string;
}

interface UserSessionRow {
  id: string;
  kind: 'MINIAPP' | 'ADMIN';
  ipAddress: string | null;
  lastUsedAt: string;
  revokedAt: string | null;
}

interface UserAuditRow {
  id: string;
  action: string;
  actorTelegramId: number;
  actorUsername: string | null;
  reason: string | null;
  createdAt: string;
}

interface UserReferralStatsDto {
  code: string;
  link: string;
  invitedCount: number;
  qualifiedCount: number;
  conversionPercent: number;
  paidOrdersCount: number;
  turnoverKopecks: number;
  totalEarnedKopecks: number;
  totalEarnedXp: number;
  byGeneration: Array<{
    level: number;
    percent: number;
    invitedCount: number;
    accrualsCount: number;
    earnedKopecks: number;
  }>;
}

interface UserFinancialSummaryDto {
  deliveredOrdersCount: number;
  grossOrdersKopecks: number;
  externalPaidOrdersKopecks: number;
  depositsRubKopecks: number;
  depositsGramMilli: number;
  depositsGramRubEquivKopecks: number;
  totalBroughtKopecks: number;
  ordersGrossMarginKopecks: number;
  referralCommissionsPaidKopecks: number;
  gamesNetProfitKopecks: number;
  netPlatformProfitKopecks: number;
}

interface LinkedAccountDto {
  userId: string;
  telegramId: number;
  username: string | null;
  firstName: string | null;
  customName?: string | null;
  photoUrl?: string | null;
  customPhotoUrl?: string | null;
  isVerified?: boolean;
  isBanned?: boolean;
  confidence: 'HIGH' | 'MEDIUM';
  score: number;
  reasons: Array<{
    kind: 'device' | 'ip' | 'payment' | 'gift' | 'referral';
    label: string;
    detail: string;
  }>;
}

interface UserCommissionSummaryDto {
  source: 'general' | 'spend_tier' | 'personal';
  starsSource: 'general' | 'spend_tier' | 'personal';
  premiumSource: 'general' | 'spend_tier' | 'personal';
  starsFeePercent: number;
  premiumFeePercent: number;
  starsMarkup: number;
  premiumMarkup: number;
  customStarsFeePercent: number | null;
  customPremiumFeePercent: number | null;
  spentRub: number;
  currentTierMinRub: number | null;
  nextTier: {
    minSpentRub: number;
    remainingRub: number;
    starsFeePercent: number;
    premiumFeePercent: number;
  } | null;
}

type UsersSortBy = 'createdAt' | 'balanceRub' | 'balanceGram' | 'deliveredOrders';
type UsersSortDir = 'asc' | 'desc';

interface AdminUserDetailDto {
  id: string;
  telegramId: number;
  username: string | null;
  firstName: string | null;
  customName?: string | null;
  photoUrl?: string | null;
  customPhotoUrl?: string | null;
  hideUsername?: boolean;
  referralCode: string;
  referralDiscountUsed?: boolean;
  firstDepositBonusUsed?: boolean;
  appliedReferralCode?: string | null;
  personalReferralCodePerks?: {
    codeId: string | null;
    firstOrderDiscountPercent: number;
    firstDepositBonusPercent: number;
    hasCustomRow?: boolean;
    active: boolean;
    usesCount: number;
    maxUses?: number | null;
  };
  referralStats?: UserReferralStatsDto;
  commission?: UserCommissionSummaryDto;
  deliveredOrdersCount?: number;
  financialSummary?: UserFinancialSummaryDto;
  linkedAccounts?: LinkedAccountDto[];
  balanceKopecks: number;
  balanceMilliGram: number;
  xp: number;
  level: number;
  levelTitle: string;
  purchasedStars: number;
  premiumCount: number;
  friendsCount: number;
  streak: number;
  activeBrainrot: string;
  ownedCompanions: string[];
  isVerified: boolean;
  roles: PlatformRole[];
  ban: {
    id: string;
    bannedAt: string;
    bannedUntil: string | null;
    reason: string;
  } | null;
  fish: {
    hunger: number;
    happiness: number;
    health: number;
    status: string;
    isDead: boolean;
    xpMultiplier: number;
    lastFedAt: string | null;
    lastPlayedAt: string | null;
  };
  recentOrders: Array<Record<string, unknown>>;
  recentTransactions: UserTransactionRow[];
  recentGameBets: Array<Record<string, unknown>>;
  recentCaseOpenings: Array<Record<string, unknown>>;
  devices: UserDeviceRow[];
  loginEvents?: UserLoginEventRow[];
  sessions: UserSessionRow[];
  auditLogs: UserAuditRow[];
}

interface UserListItem {
  id: string;
  telegramId: number;
  username: string | null;
  firstName: string | null;
  customName?: string | null;
  photoUrl?: string | null;
  tgPhotoUrl?: string | null;
  customPhotoUrl?: string | null;
  refCode?: string;
  xp: number;
  level?: number;
  levelTitle?: string;
  purchasedStars: number;
  deliveredOrdersCount?: number;
  paidOrdersKopecks?: number;
  balanceKopecks: number;
  balanceMilliGram?: number;
  activeBrainrot?: string;
  fishStatus?: string;
  fishHealth?: number;
  isVerified?: boolean;
  isBanned?: boolean;
  hasPersonalFee?: boolean;
  customStarsFeeBp?: number | null;
  customPremiumFeeBp?: number | null;
  customStarsFeePercent?: number | null;
  customPremiumFeePercent?: number | null;
  createdAt: string;
}

const COMPANION_CATALOG = [
  { id: 'clown', name: 'Клоуни' },
  { id: 'fugu', name: 'Фугу' },
  { id: 'shark', name: 'Акулёнок' },
  { id: 'koi', name: 'Кои' },
] as const;

const ALL_ROLES: { role: PlatformRole; label: string }[] = [
  { role: 'superadministrator', label: 'Суперадмин' },
  { role: 'administrator', label: 'Администратор' },
  { role: 'support', label: 'Саппорт' },
];

function formatGeoSummary(g: {
  countryCode?: string | null;
  countryName?: string | null;
  cityName?: string | null;
  regionName?: string | null;
}): string {
  // START_BLOCK_FORMAT_GEO_SUMMARY
  const parts: string[] = [];
  if (g.countryCode) parts.push(g.countryCode.toUpperCase());
  if (g.countryName) parts.push(g.countryName);
  const loc = g.cityName || g.regionName;
  if (loc && loc !== g.countryName) parts.push(loc);
  return parts.length > 0 ? parts.join(' · ') : 'Локальная / не определено';
  // END_BLOCK_FORMAT_GEO_SUMMARY
}

function SortChevron({ active, dir }: { active: boolean; dir: UsersSortDir }) {
  // START_BLOCK_SORT_CHEVRON
  return (
    <svg
      width={12}
      height={12}
      viewBox="0 0 12 12"
      fill="none"
      style={{
        opacity: active ? 1 : 0.35,
        color: active ? 'var(--accent, #38bdf8)' : 'currentColor',
        flexShrink: 0,
      }}
      aria-hidden="true"
    >
      <path
        d="M6 2.2L8.8 5H3.2L6 2.2Z"
        fill={!active || dir === 'asc' ? 'currentColor' : 'transparent'}
        stroke="currentColor"
        strokeWidth="0.8"
      />
      <path
        d="M6 9.8L3.2 7H8.8L6 9.8Z"
        fill={!active || dir === 'desc' ? 'currentColor' : 'transparent'}
        stroke="currentColor"
        strokeWidth="0.8"
      />
    </svg>
  );
  // END_BLOCK_SORT_CHEVRON
}

export const USERS_MOBILE_SORT_OPTIONS: ReadonlyArray<{
  value: `${UsersSortBy}:${UsersSortDir}`;
  label: string;
}> = [
  { value: 'createdAt:desc', label: 'Регистрация: сначала новые' },
  { value: 'createdAt:asc', label: 'Регистрация: сначала старые' },
  { value: 'balanceRub:desc', label: 'Баланс RUB: по убыванию' },
  { value: 'balanceRub:asc', label: 'Баланс RUB: по возрастанию' },
  { value: 'balanceGram:desc', label: 'Баланс GRAM: по убыванию' },
  { value: 'balanceGram:asc', label: 'Баланс GRAM: по возрастанию' },
  { value: 'deliveredOrders:desc', label: 'Выполнено заказов: больше → меньше' },
  { value: 'deliveredOrders:asc', label: 'Выполнено заказов: меньше → больше' },
];

// START_CONTRACT: parseUsersSortOption
//   PURPOSE: Parse a combined "sortBy:sortDir" select value into validated UsersSortBy and UsersSortDir with safe fallback.
//   INPUTS: { raw: string - Combined sort option string }
//   OUTPUTS: { sortBy: UsersSortBy, sortDir: UsersSortDir }
//   SIDE_EFFECTS: none
//   LINKS: M-ADMIN
// END_CONTRACT: parseUsersSortOption
export function parseUsersSortOption(raw: string): {
  sortBy: UsersSortBy;
  sortDir: UsersSortDir;
} {
  // START_BLOCK_PARSE_USERS_SORT_OPTION
  const parts = raw.split(':');
  if (parts.length !== 2) {
    return { sortBy: 'createdAt', sortDir: 'desc' };
  }
  const [col, dir] = parts;
  const validCols: ReadonlyArray<UsersSortBy> = [
    'createdAt',
    'balanceRub',
    'balanceGram',
    'deliveredOrders',
  ];
  const validDirs: ReadonlyArray<UsersSortDir> = ['asc', 'desc'];
  if (validCols.includes(col as UsersSortBy) && validDirs.includes(dir as UsersSortDir)) {
    return { sortBy: col as UsersSortBy, sortDir: dir as UsersSortDir };
  }
  return { sortBy: 'createdAt', sortDir: 'desc' };
  // END_BLOCK_PARSE_USERS_SORT_OPTION
}

// START_CONTRACT: Users
//   PURPOSE: Render the Users directory with sortable columns, completed orders count, quick filters, and the User Detail Drawer.
//   INPUTS: none
//   OUTPUTS: React element
//   SIDE_EFFECTS: Queries /admin/users and mutates user state via /admin/users/:id/*
//   LINKS: M-ADMIN
// END_CONTRACT: Users
export function Users() {
  // START_BLOCK_USERS
  const [searchParams] = useSearchParams();
  const [q, setQ] = useState(() => searchParams.get('q') ?? '');
  const [filter, setFilter] = useState<'all' | 'verified' | 'personal_fee' | 'banned' | 'dead_fish'>('all');
  const [sortBy, setSortBy] = useState<UsersSortBy>('createdAt');
  const [sortDir, setSortDir] = useState<UsersSortDir>('desc');
  const [detailId, setDetailId] = useState<string | null>(null);
  const { money } = useDisplayCurrency();

  const toggleSort = (col: UsersSortBy) => {
    if (sortBy === col) {
      setSortDir((prev) => (prev === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortBy(col);
      setSortDir('desc');
    }
  };

  useEffect(() => {
    const qParam = searchParams.get('q');
    if (qParam !== null) {
      setQ(qParam);
    }
    const fromSearch = searchParams.get('id') ?? searchParams.get('userId');
    if (fromSearch) {
      setDetailId(fromSearch);
      return;
    }
    const m = window.location.hash.match(/[?&](?:id|userId)=([^&]+)/);
    if (m?.[1]) {
      setDetailId(decodeURIComponent(m[1]));
    }
  }, [searchParams]);

  const { data, isLoading } = useQuery({
    queryKey: ['users', q, filter, sortBy, sortDir],
    queryFn: () =>
      api.get<UserListItem[]>(
        `/admin/users?q=${encodeURIComponent(q)}&filter=${filter === 'all' ? '' : filter}&sortBy=${sortBy}&sortDir=${sortDir}`,
      ),
  });

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Пользователи</h1>
          <div className="page-subtitle">
            Профили пользователей, реферальные коды и статистика, входы с устройств и ГЕО, балансы RUB/GRAM и аквариум
          </div>
        </div>
      </div>

      <div className="toolbar">
        <div className="tabs" style={{ marginBottom: 0 }}>
          <button
            type="button"
            className={`tab-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            Все пользователи
          </button>
          <button
            type="button"
            className={`tab-btn ${filter === 'verified' ? 'active' : ''}`}
            onClick={() => setFilter('verified')}
          >
            <AdminIcon name="verified" size={13} style={{ marginRight: 5 }} />
            Верифицированные
          </button>
          <button
            type="button"
            className={`tab-btn ${filter === 'personal_fee' ? 'active' : ''}`}
            onClick={() => setFilter('personal_fee')}
          >
            <AdminIcon name="sparkle" size={13} style={{ marginRight: 5 }} />
            Персональный тариф
          </button>
          <button
            type="button"
            className={`tab-btn ${filter === 'banned' ? 'active' : ''}`}
            onClick={() => setFilter('banned')}
          >
            <AdminIcon name="ban" size={13} style={{ marginRight: 5 }} />
            Заблокированные
          </button>
          <button
            type="button"
            className={`tab-btn ${filter === 'dead_fish' ? 'active' : ''}`}
            onClick={() => setFilter('dead_fish')}
          >
            <AdminIcon name="skull" size={13} style={{ marginRight: 5 }} />
            Рыбка погибла
          </button>
        </div>
        <div className="row" style={{ gap: 8, flexWrap: 'wrap', flex: '1 1 420px', minWidth: 0 }}>
          <select
            value={`${sortBy}:${sortDir}`}
            onChange={(e) => {
              const parsed = parseUsersSortOption(e.target.value);
              setSortBy(parsed.sortBy);
              setSortDir(parsed.sortDir);
            }}
            aria-label="Сортировка пользователей"
            style={{ flex: '1 1 220px', minWidth: 0 }}
          >
            {USERS_MOBILE_SORT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <input
            style={{ flex: '2 1 240px', minWidth: 0 }}
            placeholder="Поиск по нику, @username, Telegram ID, реф-коду…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
      </div>

      {isLoading ? (
        <div className="muted">Загрузка списка пользователей…</div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="table-wrap">
            <table className="mobile-cards">
              <thead>
                <tr>
                  <th>Пользователь</th>
                  <th>Telegram ID / Реф-код</th>
                  <th>Уровень / XP</th>
                  <th>Аквариум</th>
                  <th>
                    <button
                      type="button"
                      onClick={() => toggleSort('deliveredOrders')}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'inherit',
                        font: 'inherit',
                        padding: 0,
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 5,
                      }}
                    >
                      <span>Заказы</span>
                      <SortChevron active={sortBy === 'deliveredOrders'} dir={sortDir} />
                    </button>
                  </th>
                  <th>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                      <button
                        type="button"
                        onClick={() => toggleSort('balanceRub')}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'inherit',
                          font: 'inherit',
                          padding: 0,
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                        }}
                        title="Сортировать по балансу RUB"
                      >
                        <span>Баланс RUB</span>
                        <SortChevron active={sortBy === 'balanceRub'} dir={sortDir} />
                      </button>
                      <span className="muted">/</span>
                      <button
                        type="button"
                        onClick={() => toggleSort('balanceGram')}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'inherit',
                          font: 'inherit',
                          padding: 0,
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                        }}
                        title="Сортировать по балансу GRAM"
                      >
                        <span>GRAM</span>
                        <SortChevron active={sortBy === 'balanceGram'} dir={sortDir} />
                      </button>
                    </div>
                  </th>
                  <th>
                    <button
                      type="button"
                      onClick={() => toggleSort('createdAt')}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'inherit',
                        font: 'inherit',
                        padding: 0,
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 5,
                      }}
                    >
                      <span>Регистрация</span>
                      <SortChevron active={sortBy === 'createdAt'} dir={sortDir} />
                    </button>
                  </th>
                  <th style={{ textAlign: 'right' }}>Действие</th>
                </tr>
              </thead>
              <tbody>
                {(data ?? []).map((u) => {
                  const dn = resolveAdminUserDisplayName(u);
                  const hasPersonalFee = Boolean(
                    u.hasPersonalFee ||
                      (u.customStarsFeeBp !== null && u.customStarsFeeBp !== undefined) ||
                      (u.customPremiumFeeBp !== null && u.customPremiumFeeBp !== undefined) ||
                      (u.customStarsFeePercent !== null && u.customStarsFeePercent !== undefined) ||
                      (u.customPremiumFeePercent !== null &&
                        u.customPremiumFeePercent !== undefined),
                  );
                  return (
                    <tr
                      key={u.id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => setDetailId(u.id)}
                    >
                      <td className="mobile-card-primary" data-label="">
                        <div className="user-cell">
                          <UserAvatar
                            photoUrl={u.tgPhotoUrl ?? u.photoUrl}
                            customPhotoUrl={u.customPhotoUrl}
                            name={dn.primary}
                            size={36}
                          />
                          <div className="user-cell-meta">
                            <div className="user-cell-name-row">
                              <strong>{dn.primary}</strong>
                              {u.isVerified && (
                                <span title="Верифицирован">
                                  <AdminIcon name="verified" size={15} />
                                </span>
                              )}
                              {hasPersonalFee && (
                                <span
                                  className="chip"
                                  style={{
                                    background: 'rgba(139, 92, 246, 0.16)',
                                    borderColor: 'rgba(167, 139, 250, 0.38)',
                                    color: '#c4b5fd',
                                    fontWeight: 700,
                                    fontSize: 10.5,
                                    padding: '2px 7px',
                                  }}
                                  title="Установлен персональный тариф комиссии"
                                >
                                  ТАРИФ
                                </span>
                              )}
                              {dn.originalTgName && (
                                <span className="tg-orig-tag">TG: {dn.originalTgName}</span>
                              )}
                              {u.isBanned && <span className="chip banned">BAN</span>}
                            </div>
                            <div className="muted" style={{ fontSize: 12 }}>
                              {u.username ? `@${u.username}` : 'без username'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td data-label="Telegram / Реф-код">
                        <div className="mono">{u.telegramId}</div>
                        {u.refCode && (
                          <div className="muted mono" style={{ fontSize: 11.5 }}>
                            Код: {u.refCode}
                          </div>
                        )}
                      </td>
                      <td data-label="Уровень / XP">
                        <div>
                          Ур. {u.level ?? 1} · {u.levelTitle ?? 'Икринка'}
                        </div>
                        <div className="muted" style={{ fontSize: 11.5 }}>
                          {num(u.xp)} XP
                        </div>
                      </td>
                      <td data-label="Аквариум">
                        <div className="row" style={{ gap: 6 }}>
                          <CompanionIcon companionId={u.activeBrainrot} size={18} />
                          <span className={`chip ${u.fishStatus ?? 'fine'}`}>
                            {u.fishStatus === 'dead' ? (
                              <>
                                <AdminIcon name="skull" size={12} />
                                <span>Погибла</span>
                              </>
                            ) : (
                              `${u.fishHealth ?? 100}% HP`
                            )}
                          </span>
                        </div>
                      </td>
                      <td data-label="Заказов">
                        <div className="mono" style={{ fontWeight: 600 }}>
                          {num(u.deliveredOrdersCount ?? 0)} вып.
                        </div>
                        <div className="muted mono" style={{ fontSize: 11 }}>
                          {(u.paidOrdersKopecks ?? 0) > 0
                            ? `${rub(u.paidOrdersKopecks ?? 0)} ₽`
                            : 'без оплат'}
                        </div>
                      </td>
                      <td className="mono" data-label="Баланс">
                        <div>{money(u.balanceKopecks)}</div>
                        <div className="muted" style={{ fontSize: 11 }}>
                          {rub(u.balanceKopecks)} ₽ · {gram(u.balanceMilliGram ?? 0)} GRAM
                        </div>
                      </td>
                      <td className="muted" data-label="Регистрация">
                        {dt(u.createdAt)}
                      </td>
                      <td
                        className="mobile-card-actions"
                        data-label=""
                        style={{ textAlign: 'right' }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          type="button"
                          className="btn sm ghost"
                          onClick={() => setDetailId(u.id)}
                        >
                          Открыть
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {!data?.length && (
                  <tr>
                    <td colSpan={8} className="muted" style={{ textAlign: 'center', padding: 28 }}>
                      Пользователи не найдены
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {detailId && (
        <UserDetailDrawer
          id={detailId}
          onClose={() => setDetailId(null)}
          onSelectUser={(nextId) => setDetailId(nextId)}
        />
      )}
    </>
  );
  // END_BLOCK_USERS
}

type PrimaryUserTab = 'overview' | 'aquarium' | 'telemetry' | 'history';
type HistorySubTab = 'orders' | 'transactions' | 'bets' | 'cases' | 'audit';

function UserDetailDrawer({
  id,
  onClose,
  onSelectUser,
}: {
  id: string;
  onClose: () => void;
  onSelectUser: (nextId: string) => void;
}) {
  // START_BLOCK_USER_360_DRAWER
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const { money } = useDisplayCurrency();
  const [mainTab, setMainTab] = useState<PrimaryUserTab>('overview');
  const [historyTab, setHistoryTab] = useState<HistorySubTab>('orders');

  // Custom referral code & inline perks state
  const [customRefInput, setCustomRefInput] = useState('');
  const [personalOrderDiscountInput, setPersonalOrderDiscountInput] = useState('2');
  const [personalDepositBonusInput, setPersonalDepositBonusInput] = useState('0');

  // Personal commission state (empty string = inherit spend-tier / general rate)
  const [customStarsFeeInput, setCustomStarsFeeInput] = useState('');
  const [customPremiumFeeInput, setCustomPremiumFeeInput] = useState('');

  // Balance adjustment state
  const [currency, setCurrency] = useState<'RUB' | 'GRAM'>('RUB');
  const [deltaAmount, setDeltaAmount] = useState('');
  const [confirmBalanceOpen, setConfirmBalanceOpen] = useState(false);

  // XP & Streak state
  const [deltaXp, setDeltaXp] = useState('');
  const [streakInput, setStreakInput] = useState('');

  // Direct DM state
  const [dmText, setDmText] = useState('');

  // Ban modal state
  const [banDurationDays, setBanDurationDays] = useState('');
  const [confirmBanOpen, setConfirmBanOpen] = useState(false);

  // Role toggle state
  const [pendingRoles, setPendingRoles] = useState<PlatformRole[] | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['user-detail', id],
    queryFn: () =>
      api.get<AdminUserDetailDto & { orders?: Array<Record<string, unknown>> }>(
        `/admin/users/${id}`,
      ),
  });

  useEffect(() => {
    if (data?.referralCode) {
      setCustomRefInput(data.referralCode);
    }
  }, [data?.referralCode]);

  useEffect(() => {
    setPersonalOrderDiscountInput(
      String(data?.personalReferralCodePerks?.firstOrderDiscountPercent ?? 2),
    );
    setPersonalDepositBonusInput(
      String(data?.personalReferralCodePerks?.firstDepositBonusPercent ?? 0),
    );
  }, [
    data?.id,
    data?.personalReferralCodePerks?.firstOrderDiscountPercent,
    data?.personalReferralCodePerks?.firstDepositBonusPercent,
  ]);

  useEffect(() => {
    if (!data?.commission) {
      setCustomStarsFeeInput('');
      setCustomPremiumFeeInput('');
      return;
    }
    setCustomStarsFeeInput(
      data.commission.customStarsFeePercent !== null &&
        data.commission.customStarsFeePercent !== undefined
        ? String(data.commission.customStarsFeePercent)
        : '',
    );
    setCustomPremiumFeeInput(
      data.commission.customPremiumFeePercent !== null &&
        data.commission.customPremiumFeePercent !== undefined
        ? String(data.commission.customPremiumFeePercent)
        : '',
    );
  }, [
    data?.id,
    data?.commission?.customStarsFeePercent,
    data?.commission?.customPremiumFeePercent,
  ]);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['user-detail', id] });
    void qc.invalidateQueries({ queryKey: ['users'] });
    void qc.invalidateQueries({ queryKey: ['admin-referrals'] });
    void qc.invalidateQueries({ queryKey: ['admin-referral-codes'] });
  };

  const copyText = (value: string, label: string) => {
    void navigator.clipboard?.writeText(value);
    toast(`${label} скопирован`);
  };

  const refCodePerksMutation = useMutation({
    mutationFn: () => {
      const cleanRef = customRefInput.trim();
      if (cleanRef.length < 3) {
        throw new Error('Реферальный код должен содержать минимум 3 символа');
      }
      const orderDiscount = Number(String(personalOrderDiscountInput).replace(',', '.'));
      const depositBonus = Number(String(personalDepositBonusInput).replace(',', '.'));
      if (!Number.isFinite(orderDiscount) || orderDiscount < 0 || orderDiscount > 50) {
        throw new Error('Скидка на 1-й заказ должна быть от 0% до 50%');
      }
      if (!Number.isFinite(depositBonus) || depositBonus < 0 || depositBonus > 100) {
        throw new Error('Бонус к 1-му пополнению должен быть от 0% до 100%');
      }
      return api.put(`/admin/users/${id}/ref-code-perks`, {
        refCode: cleanRef,
        firstOrderDiscountPercent: orderDiscount,
        firstDepositBonusPercent: depositBonus,
        active: true,
      });
    },
    onSuccess: () => {
      toast('Личный реферальный код и бонусы сохранены');
      refresh();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Не удалось сохранить код и бонусы'),
  });

  const commissionMutation = useMutation({
    mutationFn: (payload: {
      customStarsFeePercent: number | null;
      customPremiumFeePercent: number | null;
      reason: string;
    }) => api.put<UserCommissionSummaryDto>(`/admin/users/${id}/commission`, payload),
    onSuccess: () => {
      toast('Персональная комиссия пользователя обновлена');
      refresh();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Ошибка сохранения комиссии'),
  });

  const handleSaveCommission = () => {
    const rawStars = customStarsFeeInput.trim();
    const rawPrem = customPremiumFeeInput.trim();
    let starsVal: number | null = null;
    let premVal: number | null = null;
    if (rawStars !== '') {
      const n = Number(rawStars.replace(',', '.'));
      if (!Number.isFinite(n) || n < 0 || n > 900) {
        return toast('Комиссия Stars должна быть от 0% до 900% (или пусто для авто-тарифа)');
      }
      starsVal = n;
    }
    if (rawPrem !== '') {
      const n = Number(rawPrem.replace(',', '.'));
      if (!Number.isFinite(n) || n < 0 || n > 900) {
        return toast('Комиссия Premium должна быть от 0% до 900% (или пусто для авто-тарифа)');
      }
      premVal = n;
    }
    commissionMutation.mutate({
      customStarsFeePercent: starsVal,
      customPremiumFeePercent: premVal,
      reason:
        starsVal === null && premVal === null
          ? 'Сброс персональной комиссии на общую / по обороту'
          : `Установка персональной комиссии Stars=${starsVal ?? 'авто'}%, Premium=${premVal ?? 'авто'}%`,
    });
  };

  const resetProfileMutation = useMutation({
    mutationFn: (opts: { resetName: boolean; resetAvatar: boolean }) =>
      api.post(`/admin/users/${id}/reset-profile`, {
        resetName: opts.resetName,
        resetAvatar: opts.resetAvatar,
        reason: 'Модерация кастомного профиля оператором',
      }),
    onSuccess: () => {
      toast('Кастомные данные профиля сброшены');
      refresh();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Ошибка сброса профиля'),
  });

  const fishMutation = useMutation({
    mutationFn: (action: 'golden_food' | 'revive_free' | 'feed' | 'play') =>
      api.post(`/admin/users/${id}/fish-care`, {
        action,
        reason: `Уход за рыбкой: ${action}`,
      }),
    onSuccess: () => {
      toast('Состояние рыбки обновлено');
      refresh();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Ошибка пульта рыбки'),
  });

  const companionMutation = useMutation({
    mutationFn: ({ companionId, mode }: { companionId: string; mode: 'grant' | 'revoke' }) =>
      api.post(`/admin/users/${id}/companions`, {
        companionId,
        mode,
        reason: `Оператор: ${mode} ${companionId}`,
      }),
    onSuccess: () => {
      toast('Коллекция рыбок обновлена');
      refresh();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Ошибка коллекции'),
  });

  const xpMutation = useMutation({
    mutationFn: () =>
      api.post(`/admin/users/${id}/xp`, {
        deltaXp: Number(deltaXp || 0),
        streakCount: streakInput.trim() !== '' ? Number(streakInput) : undefined,
        reason: 'Ручная корректировка XP/стрика из карточки пользователя',
      }),
    onSuccess: () => {
      toast('XP и стрик обновлены');
      setDeltaXp('');
      setStreakInput('');
      refresh();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Ошибка обновления XP'),
  });

  const verifyMutation = useMutation({
    mutationFn: (isVerified: boolean) =>
      api.post(`/admin/users/${id}/verify`, {
        isVerified,
        reason: isVerified ? 'Верификация пользователя оператором' : 'Снятие верификации',
      }),
    onSuccess: () => {
      toast('Статус верификации обновлён');
      refresh();
    },
  });

  const unbanMutation = useMutation({
    mutationFn: () =>
      api.post(`/admin/users/${id}/unban`, {
        reason: 'Разблокировка из карточки пользователя',
      }),
    onSuccess: () => {
      toast('Пользователь разблокирован');
      refresh();
    },
  });

  const dmMutation = useMutation({
    mutationFn: () =>
      api.post(`/admin/users/${id}/dm`, {
        text: dmText.trim(),
        reason: 'Личное сообщение из карточки пользователя',
      }),
    onSuccess: () => {
      toast('Сообщение отправлено в Telegram-бот');
      setDmText('');
      refresh();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Не удалось отправить сообщение'),
  });

  const handleConfirmBalance = async ({
    reason,
    stepUpGrantToken,
  }: {
    reason: string;
    stepUpGrantToken?: string;
  }) => {
    const val = parseFloat(deltaAmount.replace(',', '.'));
    if (!Number.isFinite(val) || val === 0) {
      throw new Error('Введите ненулевую сумму');
    }
    if (currency === 'GRAM') {
      await api.post('/admin/actions/execute', {
        kind: 'balance.gram_adjust',
        targetId: id,
        payload: { deltaMilliGram: Math.round(val * 1000) },
        reason,
        stepUpGrantToken,
      });
    } else {
      await api.post('/admin/actions/execute', {
        kind: 'balance.adjust',
        targetId: id,
        payload: { deltaKopecks: Math.round(val * 100) },
        reason,
        stepUpGrantToken,
      });
    }
    setDeltaAmount('');
    toast(`Баланс ${currency} скорректирован`);
    refresh();
  };

  const handleConfirmBan = async ({ reason }: { reason: string }) => {
    const days = banDurationDays.trim() ? Number(banDurationDays) : null;
    await api.post(`/admin/users/${id}/ban`, {
      reason,
      durationDays: days,
    });
    setBanDurationDays('');
    toast('Пользователь заблокирован');
    refresh();
  };

  const handleConfirmRoles = async ({
    reason,
    stepUpGrantToken,
  }: {
    reason: string;
    stepUpGrantToken?: string;
  }) => {
    if (!pendingRoles) return;
    await api.post(`/admin/users/${id}/roles`, {
      roles: pendingRoles,
      reason,
      stepUpGrantToken,
    });
    setPendingRoles(null);
    toast('RBAC роли пользователя обновлены');
    refresh();
  };

  const toggleRole = (role: PlatformRole) => {
    if (!data) return;
    const current = new Set(data.roles ?? []);
    if (current.has(role)) current.delete(role);
    else current.add(role);
    setPendingRoles(Array.from(current));
  };

  const dn = data ? resolveAdminUserDisplayName(data) : null;
  const refStats: UserReferralStatsDto = data?.referralStats ?? {
    code: data?.referralCode ?? '',
    link: `https://t.me/starsfish_game?startapp=${data?.referralCode ?? ''}`,
    invitedCount: data?.friendsCount ?? 0,
    qualifiedCount: 0,
    conversionPercent: 0,
    paidOrdersCount: 0,
    turnoverKopecks: 0,
    totalEarnedKopecks: 0,
    totalEarnedXp: 0,
    byGeneration: [],
  };
  const loginEvents = data?.loginEvents ?? [];
  const ordersRows = data?.recentOrders ?? data?.orders ?? [];
  const fin: UserFinancialSummaryDto = data?.financialSummary ?? {
    deliveredOrdersCount: data?.deliveredOrdersCount ?? 0,
    grossOrdersKopecks: 0,
    externalPaidOrdersKopecks: 0,
    depositsRubKopecks: 0,
    depositsGramMilli: 0,
    depositsGramRubEquivKopecks: 0,
    totalBroughtKopecks: 0,
    ordersGrossMarginKopecks: 0,
    referralCommissionsPaidKopecks: 0,
    gamesNetProfitKopecks: 0,
    netPlatformProfitKopecks: 0,
  };
  const linkedAccounts = data?.linkedAccounts ?? [];

  const drawerBody = (
    <div className="drawer-backdrop" onClick={onClose}>
      <div
        className="drawer-panel user-drawer-wide"
        onClick={(e) => e.stopPropagation()}
      >
        {isLoading || !data || !dn ? (
          <div className="muted" style={{ padding: 28 }}>
            Загрузка профиля пользователя…
          </div>
        ) : (
          <>
            {/* Sticky Clean Profile Header */}
            <div className="user-drawer-header">
              <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                <div className="user-cell" style={{ gap: 14, flex: '1 1 240px', minWidth: 0 }}>
                  <UserAvatar
                    photoUrl={data.photoUrl}
                    customPhotoUrl={data.customPhotoUrl}
                    name={dn.primary}
                    size={54}
                  />
                  <div className="user-cell-meta">
                    <div className="user-cell-name-row" style={{ gap: 8 }}>
                      <h2 style={{ margin: 0, fontSize: 20, overflowWrap: 'anywhere' }}>{dn.primary}</h2>
                      {data.isVerified && (
                        <span className="chip info">
                          <AdminIcon name="verified" size={14} />
                          <span>Верифицирован</span>
                        </span>
                      )}
                      {dn.originalTgName && (
                        <span className="tg-orig-tag">TG: {dn.originalTgName}</span>
                      )}
                      {data.hideUsername && (
                        <span className="chip">Скрыл @username в топе</span>
                      )}
                      {data.ban && (
                        <span className="chip banned">
                          <AdminIcon name="ban" size={12} />
                          <span>ЗАБЛОКИРОВАН</span>
                        </span>
                      )}
                    </div>
                    <div
                      className="muted mono"
                      style={{ fontSize: 12, marginTop: 3, overflowWrap: 'anywhere' }}
                    >
                      {data.username ? `@${data.username} · ` : ''}
                      TG ID: {data.telegramId} · UUID: {data.id.slice(0, 8)}… · Код:{' '}
                      <strong style={{ color: 'var(--ink)' }}>{data.referralCode}</strong>
                    </div>
                  </div>
                </div>

                <div className="row" style={{ gap: 8 }}>
                  <button
                    type="button"
                    className={`btn sm ${data.isVerified ? 'ghost' : 'primary'}`}
                    disabled={verifyMutation.isPending}
                    onClick={() => verifyMutation.mutate(!data.isVerified)}
                  >
                    <AdminIcon name="verified" size={14} />
                    <span>{data.isVerified ? 'Снять галочку' : 'Верифицировать'}</span>
                  </button>
                  {data.ban ? (
                    <button
                      type="button"
                      className="btn sm success"
                      onClick={() => unbanMutation.mutate()}
                    >
                      Разблокировать
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn sm danger"
                      onClick={() => setConfirmBanOpen(true)}
                    >
                      Заблокировать
                    </button>
                  )}
                  <button type="button" className="btn sm ghost" onClick={onClose}>
                    <AdminIcon name="close" size={13} />
                    <span>Закрыть</span>
                  </button>
                </div>
              </div>

              {/* 4 Clean Primary Tabs */}
              <div className="tabs" style={{ marginTop: 16, marginBottom: 0, borderBottom: 'none', paddingBottom: 0 }}>
                <button
                  type="button"
                  className={`tab-btn ${mainTab === 'overview' ? 'active' : ''}`}
                  onClick={() => setMainTab('overview')}
                >
                  Обзор и управление
                </button>
                <button
                  type="button"
                  className={`tab-btn ${mainTab === 'telemetry' ? 'active' : ''}`}
                  onClick={() => setMainTab('telemetry')}
                >
                  <AdminIcon name="globe" size={13} style={{ marginRight: 5 }} />
                  Входы, устройства и ГЕО ({loginEvents.length || data.devices.length})
                </button>
                <button
                  type="button"
                  className={`tab-btn ${mainTab === 'aquarium' ? 'active' : ''}`}
                  onClick={() => setMainTab('aquarium')}
                >
                  Аквариум ({Math.round(data.fish?.health ?? 100)}% HP)
                </button>
                <button
                  type="button"
                  className={`tab-btn ${mainTab === 'history' ? 'active' : ''}`}
                  onClick={() => setMainTab('history')}
                >
                  История и операции ({ordersRows.length})
                </button>
              </div>
            </div>

            <div className="user-drawer-body">
              {data.ban && (
                <div
                  className="user-section"
                  style={{
                    background: 'rgba(229, 133, 133, 0.12)',
                    borderColor: 'rgba(229, 133, 133, 0.35)',
                  }}
                >
                  <div style={{ fontWeight: 600, color: '#fca5a5' }}>
                    Активная блокировка с {dt(data.ban.bannedAt)}
                  </div>
                  <div style={{ fontSize: 13, marginTop: 4 }}>
                    Причина: <strong>{data.ban.reason}</strong> · Срок:{' '}
                    {data.ban.bannedUntil ? dt(data.ban.bannedUntil) : 'Бессрочно'}
                  </div>
                </div>
              )}

              {/* 5-Column Key Metrics Strip */}
              <div className="user-kpi-strip">
                <div className="user-kpi-item">
                  <div className="uk-label">Выполнено заказов</div>
                  <div className="uk-val">{num(fin.deliveredOrdersCount)}</div>
                  <div className="uk-sub">
                    Stars: {num(data.purchasedStars)} · Prem: {num(data.premiumCount)}
                  </div>
                </div>
                <div className="user-kpi-item">
                  <div className="uk-label">Принёс платформе</div>
                  <div className="uk-val">{money(fin.totalBroughtKopecks)}</div>
                  <div className="uk-sub">{rub(fin.totalBroughtKopecks)} ₽ оплат и пополнений</div>
                </div>
                <div className="user-kpi-item">
                  <div className="uk-label">Чистая прибыль (LTV)</div>
                  <div
                    className="uk-val"
                    style={{
                      color:
                        fin.netPlatformProfitKopecks > 0
                          ? '#4ade80'
                          : fin.netPlatformProfitKopecks < 0
                            ? '#fca5a5'
                            : undefined,
                    }}
                  >
                    {fin.netPlatformProfitKopecks > 0 ? '+' : ''}
                    {money(fin.netPlatformProfitKopecks)}
                  </div>
                  <div className="uk-sub">Маржа + игры − рефералка</div>
                </div>
                <div className="user-kpi-item">
                  <div className="uk-label">Кошельки RUB / GRAM</div>
                  <div className="uk-val">{rub(data.balanceKopecks)} ₽</div>
                  <div className="uk-sub" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <AdminIcon name="gram" size={12} />
                    <span>{gram(data.balanceMilliGram ?? 0)} GRAM</span>
                  </div>
                </div>
                <div className="user-kpi-item">
                  <div className="uk-label">Уровень и рефералка</div>
                  <div className="uk-val">Ур. {data.level} · {data.levelTitle}</div>
                  <div className="uk-sub">
                    {num(data.xp)} XP · Реф: {num(refStats.invitedCount)} ({rub(refStats.totalEarnedKopecks)} ₽)
                  </div>
                </div>
              </div>

              {/* ── TAB 1: OVERVIEW & MANAGEMENT ── */}
              {mainTab === 'overview' && (
                <>
                  {/* Platform Revenue & LTV Breakdown (#2) */}
                  <div className="user-section">
                    <div className="user-section-head">
                      <div>
                        <div className="user-section-title">Доход платформы от пользователя (LTV)</div>
                        <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                          Реальные поступления от оплат и депозитов, валовая маржа за вычетом себестоимости Fragment и чистый результат игр
                        </div>
                      </div>
                      <span className={`chip ${fin.netPlatformProfitKopecks >= 0 ? 'info' : 'severity-WARN'}`}>
                        Чистый LTV: {fin.netPlatformProfitKopecks > 0 ? '+' : ''}
                        {rub(fin.netPlatformProfitKopecks)} ₽
                      </span>
                    </div>

                    <div className="ref-stats-grid">
                      <div className="ref-stat-cell">
                        <div className="rs-label">Принёс денег (всего)</div>
                        <div className="rs-val">{rub(fin.totalBroughtKopecks)} ₽</div>
                      </div>
                      <div className="ref-stat-cell">
                        <div className="rs-label">Чистая прибыль платформе</div>
                        <div
                          className="rs-val"
                          style={{
                            color:
                              fin.netPlatformProfitKopecks > 0
                                ? '#4ade80'
                                : fin.netPlatformProfitKopecks < 0
                                  ? '#fca5a5'
                                  : undefined,
                          }}
                        >
                          {fin.netPlatformProfitKopecks > 0 ? '+' : ''}
                          {rub(fin.netPlatformProfitKopecks)} ₽
                        </div>
                      </div>
                      <div className="ref-stat-cell">
                        <div className="rs-label">Оплаты заказов (внешние)</div>
                        <div className="rs-val">
                          {rub(fin.externalPaidOrdersKopecks)} ₽{' '}
                          <span className="muted" style={{ fontSize: 11.5, fontWeight: 400 }}>
                            (оборот {rub(fin.grossOrdersKopecks)} ₽)
                          </span>
                        </div>
                      </div>
                      <div className="ref-stat-cell">
                        <div className="rs-label">Пополнения RUB / GRAM</div>
                        <div className="rs-val">
                          {rub(fin.depositsRubKopecks)} ₽ · {gram(fin.depositsGramMilli)} GRAM
                        </div>
                      </div>
                      <div className="ref-stat-cell">
                        <div className="rs-label">Маржа с заказов (после Fragment)</div>
                        <div className="rs-val">{rub(fin.ordersGrossMarginKopecks)} ₽</div>
                      </div>
                      <div className="ref-stat-cell">
                        <div className="rs-label">Игры (дом) / Реф-выплаты</div>
                        <div className="rs-val">
                          {fin.gamesNetProfitKopecks > 0 ? '+' : ''}
                          {rub(fin.gamesNetProfitKopecks)} ₽ · −{rub(fin.referralCommissionsPaidKopecks)} ₽
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Personal Commission & Spend Tier Rate */}
                  {(() => {
                    const comm = data.commission ?? {
                      source: 'general' as const,
                      starsSource: 'general' as const,
                      premiumSource: 'general' as const,
                      starsFeePercent: 10,
                      premiumFeePercent: 10,
                      starsMarkup: 1.1,
                      premiumMarkup: 1.1,
                      customStarsFeePercent: null,
                      customPremiumFeePercent: null,
                      spentRub: fin.grossOrdersKopecks / 100,
                      currentTierMinRub: null,
                      nextTier: null,
                    };
                    const sourceBadgeLabel =
                      comm.source === 'personal'
                        ? 'Персональный тариф'
                        : comm.source === 'spend_tier'
                          ? `Скидка за оборот${comm.currentTierMinRub ? ` (от ${num(comm.currentTierMinRub)} ₽)` : ''}`
                          : 'Общая комиссия';
                    const sourceBadgeTone =
                      comm.source === 'personal'
                        ? 'ok'
                        : comm.source === 'spend_tier'
                          ? 'info'
                          : '';
                    const hasCustomOverride =
                      comm.customStarsFeePercent !== null || comm.customPremiumFeePercent !== null;

                    return (
                      <div className="user-section">
                        <div className="user-section-head">
                          <div>
                            <div className="user-section-title">Комиссия и тариф пользователя</div>
                            <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                              Приоритет расчёта цены: <b>персональная комиссия</b> →{' '}
                              <b>сниженная за оборот</b> → <b>общая комиссия</b> (0% = себестоимость Fragment ×1.00)
                            </div>
                          </div>
                          <span className={`chip ${sourceBadgeTone}`}>{sourceBadgeLabel}</span>
                        </div>

                        <div className="ref-stats-grid" style={{ marginBottom: 14 }}>
                          <div className="ref-stat-cell">
                            <div className="rs-label">Итоговая комиссия Stars</div>
                            <div className="rs-val">
                              {comm.starsFeePercent}%{' '}
                              <span className="muted" style={{ fontSize: 11.5, fontWeight: 400 }}>
                                (×{comm.starsMarkup.toFixed(2)} ·{' '}
                                {comm.starsSource === 'personal'
                                  ? 'персональная'
                                  : comm.starsSource === 'spend_tier'
                                    ? 'за оборот'
                                    : 'общая'}
                                )
                              </span>
                            </div>
                          </div>
                          <div className="ref-stat-cell">
                            <div className="rs-label">Итоговая комиссия Premium</div>
                            <div className="rs-val">
                              {comm.premiumFeePercent}%{' '}
                              <span className="muted" style={{ fontSize: 11.5, fontWeight: 400 }}>
                                (×{comm.premiumMarkup.toFixed(2)} ·{' '}
                                {comm.premiumSource === 'personal'
                                  ? 'персональная'
                                  : comm.premiumSource === 'spend_tier'
                                    ? 'за оборот'
                                    : 'общая'}
                                )
                              </span>
                            </div>
                          </div>
                          <div className="ref-stat-cell">
                            <div className="rs-label">Оборот покупок (для уровней)</div>
                            <div className="rs-val">
                              {rub(Math.round(comm.spentRub * 100))} ₽
                              {comm.nextTier && (
                                <div className="muted" style={{ fontSize: 11, fontWeight: 400, marginTop: 2 }}>
                                  Ещё {rub(Math.round(comm.nextTier.remainingRub * 100))} ₽ до уровня{' '}
                                  {comm.nextTier.starsFeePercent}%
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="ref-stat-cell">
                            <div className="rs-label">Привязанный код и бонусы</div>
                            <div className="rs-val" style={{ fontSize: 13 }}>
                              {data.appliedReferralCode ? (
                                <span className="mono">{data.appliedReferralCode}</span>
                              ) : (
                                <span className="muted">Без промокода</span>
                              )}
                              <div className="muted" style={{ fontSize: 11, fontWeight: 400, marginTop: 2 }}>
                                1-й заказ: {data.referralDiscountUsed ? 'использован' : 'доступен'} · 1-й депозит:{' '}
                                {data.firstDepositBonusUsed ? 'использован' : 'доступен'}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div
                          className="row"
                          style={{
                            gap: 12,
                            alignItems: 'flex-end',
                            flexWrap: 'wrap',
                            paddingTop: 10,
                            borderTop: '1px solid var(--hair)',
                          }}
                        >
                          <label
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 4,
                              maxWidth: '100%',
                              minWidth: 0,
                            }}
                          >
                            <span className="muted" style={{ fontSize: 11.5 }}>
                              Персональная Stars (%)
                            </span>
                            <input
                              type="number"
                              min="0"
                              max="900"
                              step="0.5"
                              style={{ width: 175, maxWidth: '100%' }}
                              placeholder="Авто (общая / оборот)"
                              value={customStarsFeeInput}
                              onChange={(e) => setCustomStarsFeeInput(e.target.value)}
                            />
                          </label>
                          <label
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 4,
                              maxWidth: '100%',
                              minWidth: 0,
                            }}
                          >
                            <span className="muted" style={{ fontSize: 11.5 }}>
                              Персональная Premium (%)
                            </span>
                            <input
                              type="number"
                              min="0"
                              max="900"
                              step="0.5"
                              style={{ width: 185, maxWidth: '100%' }}
                              placeholder="Авто (общая / оборот)"
                              value={customPremiumFeeInput}
                              onChange={(e) => setCustomPremiumFeeInput(e.target.value)}
                            />
                          </label>
                          <button
                            type="button"
                            className="btn sm primary"
                            disabled={commissionMutation.isPending}
                            onClick={handleSaveCommission}
                          >
                            <AdminIcon name="check" size={13} />
                            <span>Сохранить комиссию</span>
                          </button>
                          {hasCustomOverride && (
                            <button
                              type="button"
                              className="btn sm ghost"
                              disabled={commissionMutation.isPending}
                              onClick={() => {
                                setCustomStarsFeeInput('');
                                setCustomPremiumFeeInput('');
                                commissionMutation.mutate({
                                  customStarsFeePercent: null,
                                  customPremiumFeePercent: null,
                                  reason: 'Сброс персональной комиссии на общую / по обороту',
                                });
                              }}
                            >
                              <AdminIcon name="reset" size={13} />
                              <span>Сбросить на общую</span>
                            </button>
                          )}
                          <button
                            type="button"
                            className="btn sm ghost"
                            style={{ marginLeft: 'auto', maxWidth: '100%', whiteSpace: 'normal', textAlign: 'left' }}
                            onClick={() => {
                              onClose();
                              navigate('/pricing?tab=tiers');
                            }}
                          >
                            <span>Настроить общие ступени за оборот →</span>
                          </button>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Potentially Linked Accounts (#5) */}
                  <div className="user-section">
                    <div className="user-section-head">
                      <div>
                        <div className="user-section-title">Потенциально связанные аккаунты</div>
                        <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                          Автоматическая сверка по отпечаткам устройств, публичным IP, плательщикам СБП/Т-Банка и подаркам
                        </div>
                      </div>
                      <span
                        className={`chip ${
                          linkedAccounts.some((a) => a.confidence === 'HIGH')
                            ? 'severity-WARN'
                            : linkedAccounts.length > 0
                              ? 'info'
                              : ''
                        }`}
                      >
                        {linkedAccounts.length > 0
                          ? `Найдено: ${linkedAccounts.length}`
                          : 'Совпадений нет'}
                      </span>
                    </div>

                    {linkedAccounts.length === 0 ? (
                      <div className="muted" style={{ fontSize: 12.5 }}>
                        Пересечений по отпечаткам устройств, публичным IP-адресам, плательщикам СБП и подаркам с другими пользователями не обнаружено.
                      </div>
                    ) : (
                      <div className="table-wrap">
                        <table>
                          <thead>
                            <tr>
                              <th>Аккаунт</th>
                              <th>Вероятность</th>
                              <th>Причины совпадения</th>
                              <th style={{ textAlign: 'right' }}>Действие</th>
                            </tr>
                          </thead>
                          <tbody>
                            {linkedAccounts.map((acc) => {
                              const accName = resolveAdminUserDisplayName(acc);
                              return (
                                <tr key={acc.userId}>
                                  <td>
                                    <div className="user-cell">
                                      <UserAvatar
                                        photoUrl={acc.photoUrl}
                                        customPhotoUrl={acc.customPhotoUrl}
                                        name={accName.primary}
                                        size={32}
                                      />
                                      <div className="user-cell-meta">
                                        <div className="user-cell-name-row">
                                          <strong>{accName.primary}</strong>
                                          {acc.isVerified && (
                                            <AdminIcon name="verified" size={13} />
                                          )}
                                          {acc.isBanned && (
                                            <span className="chip banned">BAN</span>
                                          )}
                                        </div>
                                        <div className="muted mono" style={{ fontSize: 11.5 }}>
                                          {acc.username ? `@${acc.username} · ` : ''}
                                          TG: {acc.telegramId}
                                        </div>
                                      </div>
                                    </div>
                                  </td>
                                  <td>
                                    <span
                                      className={`chip ${
                                        acc.confidence === 'HIGH' ? 'severity-WARN' : 'info'
                                      }`}
                                    >
                                      {acc.confidence === 'HIGH' ? 'Высокая' : 'Средняя'} ({acc.score}%)
                                    </span>
                                  </td>
                                  <td>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                      {acc.reasons.map((r, idx) => (
                                        <span
                                          key={`${r.kind}-${idx}`}
                                          className="chip"
                                          style={{ fontSize: 11.5 }}
                                        >
                                          <strong>{r.label}:</strong>&nbsp;{r.detail}
                                        </span>
                                      ))}
                                    </div>
                                  </td>
                                  <td style={{ textAlign: 'right' }}>
                                    <button
                                      type="button"
                                      className="btn sm ghost"
                                      onClick={() => onSelectUser(acc.userId)}
                                    >
                                      Открыть карточку
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                  {/* Referral Code Editor & Full Statistics (#7 & #12) */}
                  <div className="user-section">
                    <div className="user-section-head">
                      <div>
                        <div className="user-section-title">Личный реферальный код и бонусы приглашённым</div>
                        <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                          Кастомный код (3–24 символа A–Z, 0–9, _, -) и индивидуальные привилегии для приглашённых друзей
                        </div>
                      </div>
                      <button
                        type="button"
                        className="btn sm ghost"
                        onClick={() => copyText(refStats.link, 'Реферальная ссылка')}
                      >
                        <AdminIcon name="copy" size={13} />
                        <span>Скопировать ссылку</span>
                      </button>
                    </div>

                    <div
                      className="row"
                      style={{
                        gap: 12,
                        alignItems: 'flex-end',
                        flexWrap: 'wrap',
                        marginBottom: 12,
                      }}
                    >
                      <label
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 4,
                          maxWidth: '100%',
                          minWidth: 0,
                        }}
                      >
                        <span className="muted" style={{ fontSize: 11.5 }}>
                          Реферальный код
                        </span>
                        <input
                          style={{ width: 185, maxWidth: '100%' }}
                          className="mono"
                          placeholder="Например: KRL76"
                          value={customRefInput}
                          onChange={(e) => setCustomRefInput(e.target.value.toUpperCase())}
                        />
                      </label>
                      <label
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 4,
                          maxWidth: '100%',
                          minWidth: 0,
                        }}
                      >
                        <span className="muted" style={{ fontSize: 11.5 }}>
                          Скидка приглашённому на 1-й заказ (%)
                        </span>
                        <input
                          type="number"
                          min="0"
                          max="50"
                          step="0.5"
                          style={{ width: 230, maxWidth: '100%' }}
                          value={personalOrderDiscountInput}
                          onChange={(e) => setPersonalOrderDiscountInput(e.target.value)}
                        />
                      </label>
                      <label
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 4,
                          maxWidth: '100%',
                          minWidth: 0,
                        }}
                      >
                        <span className="muted" style={{ fontSize: 11.5 }}>
                          Бонус приглашённому к 1-му пополнению (%)
                        </span>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="1"
                          style={{ width: 250, maxWidth: '100%' }}
                          value={personalDepositBonusInput}
                          onChange={(e) => setPersonalDepositBonusInput(e.target.value)}
                        />
                      </label>
                      <button
                        type="button"
                        className="btn sm primary"
                        disabled={refCodePerksMutation.isPending || customRefInput.trim().length < 3}
                        onClick={() => refCodePerksMutation.mutate()}
                      >
                        <AdminIcon name="check" size={13} />
                        <span>Сохранить код и бонусы</span>
                      </button>
                    </div>
                    <div
                      className="muted mono"
                      style={{ fontSize: 12, marginBottom: 12, overflowWrap: 'anywhere' }}
                    >
                      {refStats.link}
                    </div>

                    <div className="ref-stats-grid">
                      <div className="ref-stat-cell">
                        <div className="rs-label">Регистраций по коду</div>
                        <div className="rs-val">{num(refStats.invitedCount)}</div>
                      </div>
                      <div className="ref-stat-cell">
                        <div className="rs-label">Совершили покупку</div>
                        <div className="rs-val">{num(refStats.qualifiedCount)}</div>
                      </div>
                      <div className="ref-stat-cell">
                        <div className="rs-label">Конверсия в покупку</div>
                        <div className="rs-val">{refStats.conversionPercent.toFixed(1)}%</div>
                      </div>
                      <div className="ref-stat-cell">
                        <div className="rs-label">Оплаченных заказов</div>
                        <div className="rs-val">{num(refStats.paidOrdersCount)}</div>
                      </div>
                      <div className="ref-stat-cell">
                        <div className="rs-label">Оборот рефералов</div>
                        <div className="rs-val">{rub(refStats.turnoverKopecks)} ₽</div>
                      </div>
                      <div className="ref-stat-cell">
                        <div className="rs-label">Заработано всего</div>
                        <div className="rs-val">
                          {rub(refStats.totalEarnedKopecks)} ₽ · {num(refStats.totalEarnedXp)} XP
                        </div>
                      </div>
                    </div>

                    {refStats.byGeneration.length > 0 && (
                      <div className="table-wrap">
                        <table>
                          <thead>
                            <tr>
                              <th>Поколение</th>
                              <th>Ставка</th>
                              <th>Людей в колене</th>
                              <th>Начислений</th>
                              <th>Заработано</th>
                            </tr>
                          </thead>
                          <tbody>
                            {refStats.byGeneration.map((g) => (
                              <tr key={g.level}>
                                <td>
                                  <span className="chip info">Поколение {g.level}</span>
                                </td>
                                <td className="mono">{g.percent}%</td>
                                <td className="mono">{num(g.invitedCount)}</td>
                                <td className="mono">{num(g.accrualsCount)}</td>
                                <td className="mono">{rub(g.earnedKopecks)} ₽</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  {/* Custom Profile Nickname & Avatar Moderation (#9) */}
                  {(data.customName || data.customPhotoUrl) && (
                    <div className="user-section">
                      <div className="user-section-head" style={{ marginBottom: 8 }}>
                        <div>
                          <div className="user-section-title">Кастомный профиль в Mini App</div>
                          <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                            Имя в Telegram: <strong>{data.firstName ?? '—'}</strong> · Кастомный ник:{' '}
                            <strong>{data.customName ?? 'не изменён'}</strong>
                          </div>
                        </div>
                        <div className="row" style={{ gap: 8 }}>
                          {data.customName && (
                            <button
                              type="button"
                              className="btn sm ghost"
                              disabled={resetProfileMutation.isPending}
                              onClick={() =>
                                resetProfileMutation.mutate({ resetName: true, resetAvatar: false })
                              }
                            >
                              <AdminIcon name="reset" size={13} />
                              <span>Сбросить ник</span>
                            </button>
                          )}
                          {data.customPhotoUrl && (
                            <button
                              type="button"
                              className="btn sm ghost"
                              disabled={resetProfileMutation.isPending}
                              onClick={() =>
                                resetProfileMutation.mutate({ resetName: false, resetAvatar: true })
                              }
                            >
                              <AdminIcon name="reset" size={13} />
                              <span>Сбросить аватарку</span>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Balance Adjust + RBAC + Direct Bot DM */}
                  <div className="grid cols-2" style={{ gap: 14 }}>
                    <div className="user-section">
                      <div className="user-section-title" style={{ marginBottom: 10 }}>
                        Корректировка баланса (Step-Up)
                      </div>
                      <div className="row" style={{ gap: 8 }}>
                        <select
                          value={currency}
                          onChange={(e) => setCurrency(e.target.value as 'RUB' | 'GRAM')}
                        >
                          <option value="RUB">₽ RUB</option>
                          <option value="GRAM">GRAM</option>
                        </select>
                        <input
                          style={{ flex: '1 1 120px', minWidth: 0 }}
                          placeholder="Сумма (+ или -)"
                          value={deltaAmount}
                          onChange={(e) => setDeltaAmount(e.target.value)}
                        />
                        <button
                          type="button"
                          className="btn primary"
                          disabled={!deltaAmount.trim()}
                          onClick={() => setConfirmBalanceOpen(true)}
                        >
                          Применить
                        </button>
                      </div>

                      <div className="user-section-title" style={{ marginTop: 16, marginBottom: 8 }}>
                        Роли оператора (RBAC)
                      </div>
                      <div className="row" style={{ gap: 6 }}>
                        {ALL_ROLES.map((r) => {
                          const active = (data.roles ?? []).includes(r.role);
                          return (
                            <button
                              key={r.role}
                              type="button"
                              className={`btn sm ${active ? 'primary' : 'ghost'}`}
                              onClick={() => toggleRole(r.role)}
                            >
                              {active ? <AdminIcon name="check" size={12} /> : <span>+</span>}
                              <span>{r.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="user-section">
                      <div className="user-section-title" style={{ marginBottom: 10 }}>
                        Личное сообщение от бота (Direct DM)
                      </div>
                      <textarea
                        rows={3}
                        style={{ width: '100%', marginBottom: 8 }}
                        placeholder="Введите сообщение пользователю — бот доставит его в Telegram…"
                        value={dmText}
                        onChange={(e) => setDmText(e.target.value)}
                      />
                      <div className="row" style={{ justifyContent: 'flex-end' }}>
                        <button
                          type="button"
                          className="btn sm primary"
                          disabled={!dmText.trim() || dmMutation.isPending}
                          onClick={() => dmMutation.mutate()}
                        >
                          <AdminIcon name="send" size={13} />
                          <span>{dmMutation.isPending ? 'Отправка…' : 'Отправить в Telegram'}</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* ── TAB 2: TELEMETRY, DEVICES & IP-GEO (#11) ── */}
              {mainTab === 'telemetry' && (
                <>
                  <div className="user-section">
                    <div className="user-section-head">
                      <div>
                        <div className="user-section-title">
                          Уникальные устройства пользователя ({data.devices.length})
                        </div>
                        <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                          Клиент Telegram, операционная система, модель устройства, разрешение экрана, провайдер и ГЕО по IP
                        </div>
                      </div>
                    </div>

                    {data.devices.length === 0 ? (
                      <div className="muted" style={{ padding: '8px 0' }}>
                        Данные об устройствах появятся при следующем открытии Mini App или входе в панель
                      </div>
                    ) : (
                      <div className="device-grid">
                        {data.devices.map((d) => (
                          <div className="device-tile" key={d.id}>
                            <div className="row" style={{ justifyContent: 'space-between' }}>
                              <span style={{ fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                <AdminIcon name="device" size={14} />
                                <span>
                                  {d.telegramClient ?? d.platform ?? 'Telegram Client'}
                                  {d.telegramVersion ? ` v${d.telegramVersion}` : ''}
                                </span>
                              </span>
                              <span className="mono muted" style={{ fontSize: 11 }}>
                                #{d.deviceFingerprint.slice(0, 8)}
                              </span>
                            </div>

                            <div className="muted" style={{ fontSize: 12.5 }}>
                              {[d.osName, d.deviceModel, d.browserName].filter(Boolean).join(' · ') ||
                                'Устройство не определено'}
                            </div>

                            <div className="row" style={{ gap: 6 }}>
                              <span className="geo-pill">
                                <AdminIcon name="globe" size={12} />
                                <span>{formatGeoSummary(d)}</span>
                              </span>
                              {d.ipAddress && <span className="chip mono">{d.ipAddress}</span>}
                            </div>

                            <div className="muted" style={{ fontSize: 11.5 }}>
                              {[
                                d.ispName ? `ISP: ${d.ispName}` : null,
                                d.timezone ? `TZ: ${d.timezone}` : null,
                                d.screenResolution ? `Экран: ${d.screenResolution}` : null,
                                d.languageCode ? `Язык: ${d.languageCode.toUpperCase()}` : null,
                              ]
                                .filter(Boolean)
                                .join(' · ')}
                            </div>

                            <div className="muted" style={{ fontSize: 11 }}>
                              Последний вход: {dt(d.lastSeenAt)}
                              {d.firstSeenAt ? ` · Первый: ${dt(d.firstSeenAt)}` : ''}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="user-section" style={{ padding: 0, overflow: 'hidden' }}>
                    <div style={{ padding: '14px 18px 10px' }}>
                      <div className="user-section-title">
                        Журнал входов и сессий ({loginEvents.length})
                      </div>
                    </div>
                    <div className="table-wrap">
                      <table>
                        <thead>
                          <tr>
                            <th>Время входа</th>
                            <th>Источник / Клиент</th>
                            <th>ОС, устройство, экран</th>
                            <th>IP-адрес</th>
                            <th>ГЕО, провайдер, TZ</th>
                          </tr>
                        </thead>
                        <tbody>
                          {loginEvents.map((ev) => (
                            <tr key={ev.id}>
                              <td className="muted" style={{ whiteSpace: 'nowrap' }}>
                                {dt(ev.createdAt)}
                              </td>
                              <td>
                                <div className="row" style={{ gap: 6 }}>
                                  <span className="chip info">{ev.source}</span>
                                  <strong>
                                    {ev.telegramClient ?? 'Telegram'}
                                    {ev.telegramVersion ? ` ${ev.telegramVersion}` : ''}
                                  </strong>
                                  {ev.isTelegramPremium && (
                                    <span className="chip">
                                      <AdminIcon name="premium" size={12} />
                                      <span>TG Premium</span>
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td>
                                <div>
                                  {[ev.osName, ev.deviceModel].filter(Boolean).join(' · ') || '—'}
                                </div>
                                <div className="muted" style={{ fontSize: 11.5 }}>
                                  {[
                                    ev.browserName,
                                    ev.screenResolution,
                                    ev.languageCode ? `lang:${ev.languageCode}` : null,
                                  ]
                                    .filter(Boolean)
                                    .join(' · ')}
                                </div>
                              </td>
                              <td className="mono">{ev.ipAddress ?? '—'}</td>
                              <td>
                                <div className="geo-pill">
                                  <AdminIcon name="globe" size={12} />
                                  <span>{formatGeoSummary(ev)}</span>
                                </div>
                                {(ev.ispName || ev.timezone) && (
                                  <div className="muted" style={{ fontSize: 11, marginTop: 3 }}>
                                    {[ev.ispName, ev.timezone].filter(Boolean).join(' · ')}
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                          {loginEvents.length === 0 && (
                            <tr>
                              <td colSpan={5} className="muted" style={{ textAlign: 'center', padding: 24 }}>
                                Записей о входах пока нет — появятся при следующем запуске Mini App пользователем
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}

              {/* ── TAB 3: AQUARIUM / TAMAGOTCHI ── */}
              {mainTab === 'aquarium' && (
                <div className="fish-console">
                  <div className="row" style={{ justifyContent: 'space-between', marginBottom: 12 }}>
                    <div className="row" style={{ gap: 10 }}>
                      <CompanionIcon companionId={data.activeBrainrot} size={28} />
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 15 }}>
                          Активная рыбка: {data.activeBrainrot}
                        </div>
                        <div className="muted" style={{ fontSize: 12 }}>
                          Множитель опыта: <strong>×{data.fish?.xpMultiplier ?? 1}</strong> · Покормлена:{' '}
                          {dt(data.fish?.lastFedAt ?? null)}
                        </div>
                      </div>
                    </div>
                    <span className={`chip ${data.fish?.status ?? 'fine'}`}>
                      {data.fish?.isDead ? (
                        <>
                          <AdminIcon name="skull" size={12} />
                          <span>ПОГИБЛА</span>
                        </>
                      ) : (
                        (data.fish?.status ?? 'fine').toUpperCase()
                      )}
                    </span>
                  </div>

                  <div className="grid cols-3" style={{ gap: 12, marginBottom: 14 }}>
                    <div>
                      <div className="row" style={{ justifyContent: 'space-between', fontSize: 12 }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                          <AdminIcon name="food" size={13} />
                          <span>Сытость</span>
                        </span>
                        <strong className="mono">{Math.round(data.fish?.hunger ?? 100)}%</strong>
                      </div>
                      <div className="fish-bar-track">
                        <div
                          className="fish-bar-fill"
                          style={{
                            width: `${Math.min(100, Math.max(0, data.fish?.hunger ?? 100))}%`,
                            background: 'linear-gradient(90deg, #f59e0b, #fbbf24)',
                          }}
                        />
                      </div>
                    </div>
                    <div>
                      <div className="row" style={{ justifyContent: 'space-between', fontSize: 12 }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                          <AdminIcon name="sparkle" size={13} style={{ color: '#60a5fa' }} />
                          <span>Настроение</span>
                        </span>
                        <strong className="mono">{Math.round(data.fish?.happiness ?? 100)}%</strong>
                      </div>
                      <div className="fish-bar-track">
                        <div
                          className="fish-bar-fill"
                          style={{
                            width: `${Math.min(100, Math.max(0, data.fish?.happiness ?? 100))}%`,
                            background: 'linear-gradient(90deg, #3b82f6, #60a5fa)',
                          }}
                        />
                      </div>
                    </div>
                    <div>
                      <div className="row" style={{ justifyContent: 'space-between', fontSize: 12 }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                          <AdminIcon name="heart" size={13} style={{ color: '#34d399' }} />
                          <span>Здоровье</span>
                        </span>
                        <strong className="mono">{Math.round(data.fish?.health ?? 100)}%</strong>
                      </div>
                      <div className="fish-bar-track">
                        <div
                          className="fish-bar-fill"
                          style={{
                            width: `${Math.min(100, Math.max(0, data.fish?.health ?? 100))}%`,
                            background: 'linear-gradient(90deg, #10b981, #34d399)',
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row" style={{ gap: 8, marginBottom: 16 }}>
                    <button
                      type="button"
                      className="btn sm primary"
                      disabled={fishMutation.isPending}
                      onClick={() => fishMutation.mutate('golden_food')}
                    >
                      <AdminIcon name="star" size={14} />
                      <span>Золотой корм (100%)</span>
                    </button>
                    <button
                      type="button"
                      className="btn sm success"
                      disabled={fishMutation.isPending}
                      onClick={() => fishMutation.mutate('revive_free')}
                    >
                      <AdminIcon name="heart" size={13} />
                      <span>Воскресить бесплатно</span>
                    </button>
                    <button
                      type="button"
                      className="btn sm"
                      disabled={fishMutation.isPending}
                      onClick={() => fishMutation.mutate('feed')}
                    >
                      <AdminIcon name="food" size={13} />
                      <span>Покормить (+35%)</span>
                    </button>
                    <button
                      type="button"
                      className="btn sm"
                      disabled={fishMutation.isPending}
                      onClick={() => fishMutation.mutate('play')}
                    >
                      <AdminIcon name="sparkle" size={13} />
                      <span>Поиграть (+30%)</span>
                    </button>
                  </div>

                  <div className="row" style={{ justifyContent: 'space-between', gap: 12 }}>
                    <div className="row" style={{ gap: 8 }}>
                      <span className="muted" style={{ fontSize: 12 }}>
                        Коллекция рыбок:
                      </span>
                      {COMPANION_CATALOG.map((c) => {
                        const owned = (data.ownedCompanions ?? []).includes(c.id);
                        return (
                          <button
                            key={c.id}
                            type="button"
                            className={`btn sm ${owned ? 'primary' : 'ghost'}`}
                            disabled={companionMutation.isPending || c.id === 'clown'}
                            onClick={() =>
                              companionMutation.mutate({
                                companionId: c.id,
                                mode: owned ? 'revoke' : 'grant',
                              })
                            }
                          >
                            <CompanionIcon companionId={c.id} size={15} />
                            <span>{c.name}</span>
                            {owned ? <AdminIcon name="check" size={12} /> : <span>+</span>}
                          </button>
                        );
                      })}
                    </div>

                    <div className="row" style={{ gap: 6 }}>
                      <input
                        style={{ width: 95, padding: '6px 9px' }}
                        placeholder="±XP (500)"
                        value={deltaXp}
                        onChange={(e) => setDeltaXp(e.target.value)}
                      />
                      <input
                        style={{ width: 90, padding: '6px 9px' }}
                        placeholder="Стрик дн."
                        value={streakInput}
                        onChange={(e) => setStreakInput(e.target.value)}
                      />
                      <button
                        type="button"
                        className="btn sm"
                        disabled={xpMutation.isPending}
                        onClick={() => xpMutation.mutate()}
                      >
                        Обновить XP
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* ── TAB 4: ACTIVITY HISTORY ── */}
              {mainTab === 'history' && (
                <>
                  <div className="tabs" style={{ marginBottom: 4 }}>
                    <button
                      type="button"
                      className={`tab-btn ${historyTab === 'orders' ? 'active' : ''}`}
                      onClick={() => setHistoryTab('orders')}
                    >
                      Заказы ({ordersRows.length})
                    </button>
                    <button
                      type="button"
                      className={`tab-btn ${historyTab === 'transactions' ? 'active' : ''}`}
                      onClick={() => setHistoryTab('transactions')}
                    >
                      Транзакции ({(data.recentTransactions ?? []).length})
                    </button>
                    <button
                      type="button"
                      className={`tab-btn ${historyTab === 'bets' ? 'active' : ''}`}
                      onClick={() => setHistoryTab('bets')}
                    >
                      Crash ({(data.recentGameBets ?? []).length})
                    </button>
                    <button
                      type="button"
                      className={`tab-btn ${historyTab === 'cases' ? 'active' : ''}`}
                      onClick={() => setHistoryTab('cases')}
                    >
                      Кейсы ({(data.recentCaseOpenings ?? []).length})
                    </button>
                    <button
                      type="button"
                      className={`tab-btn ${historyTab === 'audit' ? 'active' : ''}`}
                      onClick={() => setHistoryTab('audit')}
                    >
                      Аудит ({(data.auditLogs ?? []).length})
                    </button>
                  </div>

                  <div className="user-section" style={{ padding: 0, overflow: 'hidden' }}>
                    {historyTab === 'orders' && (
                      <div className="table-wrap">
                        <table>
                          <thead>
                            <tr>
                              <th>ID</th>
                              <th>Статус</th>
                              <th>Сумма</th>
                              <th>Дата</th>
                            </tr>
                          </thead>
                          <tbody>
                            {ordersRows.map((o, idx) => (
                              <tr key={String(o.id ?? idx)}>
                                <td className="mono">
                                  {String(o.shortId ?? String(o.id ?? '').slice(0, 8))}
                                </td>
                                <td>
                                  <span className={`chip ${String(o.status)}`}>{String(o.status)}</span>
                                </td>
                                <td className="mono">{rub(Number(o.amountKopecks ?? 0))} ₽</td>
                                <td className="muted">{dt(String(o.createdAt ?? ''))}</td>
                              </tr>
                            ))}
                            {ordersRows.length === 0 && (
                              <tr>
                                <td colSpan={4} className="muted" style={{ textAlign: 'center', padding: 20 }}>
                                  Заказов нет
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {historyTab === 'transactions' && (
                      <div className="table-wrap">
                        <table>
                          <thead>
                            <tr>
                              <th>Валюта / Тип</th>
                              <th>Сумма</th>
                              <th>После</th>
                              <th>Описание</th>
                              <th>Дата</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(data.recentTransactions ?? []).map((tx) => (
                              <tr key={tx.id}>
                                <td>
                                  <span className="chip info">{tx.currency}</span>{' '}
                                  <span className="mono" style={{ fontSize: 12 }}>
                                    {tx.type}
                                  </span>
                                </td>
                                <td className="mono">
                                  {tx.currency === 'GRAM'
                                    ? `${gram(tx.amountKopecks)} GRAM`
                                    : `${rub(tx.amountKopecks)} ₽`}
                                </td>
                                <td className="mono muted">
                                  {tx.currency === 'GRAM'
                                    ? `${gram(tx.balanceAfterKopecks)} GRAM`
                                    : `${rub(tx.balanceAfterKopecks)} ₽`}
                                </td>
                                <td>{tx.description}</td>
                                <td className="muted">{dt(tx.createdAt)}</td>
                              </tr>
                            ))}
                            {!(data.recentTransactions ?? []).length && (
                              <tr>
                                <td colSpan={5} className="muted" style={{ textAlign: 'center', padding: 20 }}>
                                  Транзакций нет
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {historyTab === 'bets' && (
                      <div className="table-wrap">
                        <table>
                          <thead>
                            <tr>
                              <th>Ставка</th>
                              <th>Множитель</th>
                              <th>Выплата</th>
                              <th>Статус</th>
                              <th>Дата</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(data.recentGameBets ?? []).map((b, idx) => (
                              <tr key={String(b.id ?? idx)}>
                                <td className="mono">{rub(Number(b.amountKopecks ?? 0))} ₽</td>
                                <td className="mono">
                                  {b.cashoutMultiplier ? `×${String(b.cashoutMultiplier)}` : '—'}
                                </td>
                                <td className="mono">{rub(Number(b.payoutKopecks ?? 0))} ₽</td>
                                <td>
                                  <span className="chip">{String(b.status ?? '')}</span>
                                </td>
                                <td className="muted">{dt(String(b.createdAt ?? ''))}</td>
                              </tr>
                            ))}
                            {!(data.recentGameBets ?? []).length && (
                              <tr>
                                <td colSpan={5} className="muted" style={{ textAlign: 'center', padding: 20 }}>
                                  Ставок в Crash нет
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {historyTab === 'cases' && (
                      <div className="table-wrap">
                        <table>
                          <thead>
                            <tr>
                              <th>Кейс</th>
                              <th>Стоимость</th>
                              <th>Награда</th>
                              <th>Дата</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(data.recentCaseOpenings ?? []).map((c, idx) => (
                              <tr key={String(c.id ?? idx)}>
                                <td className="mono">{String(c.caseId ?? '')}</td>
                                <td className="mono">{rub(Number(c.costKopecks ?? 0))} ₽</td>
                                <td>
                                  <span className="chip info">{String(c.rewardType ?? '')}</span>{' '}
                                  <strong>{String(c.rewardValue ?? '')}</strong>
                                </td>
                                <td className="muted">{dt(String(c.createdAt ?? ''))}</td>
                              </tr>
                            ))}
                            {!(data.recentCaseOpenings ?? []).length && (
                              <tr>
                                <td colSpan={4} className="muted" style={{ textAlign: 'center', padding: 20 }}>
                                  Открытий кейсов нет
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {historyTab === 'audit' && (
                      <div className="table-wrap">
                        <table>
                          <thead>
                            <tr>
                              <th>Действие</th>
                              <th>Оператор</th>
                              <th>Причина</th>
                              <th>Дата</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(data.auditLogs ?? []).map((log) => {
                              const meta = describeAuditAction(log.action);
                              return (
                                <tr key={log.id}>
                                  <td>
                                    <span className="chip info">{meta.title}</span>
                                  </td>
                                  <td className="mono">
                                    {log.actorUsername
                                      ? `@${log.actorUsername}`
                                      : `#${log.actorTelegramId}`}
                                  </td>
                                  <td>{log.reason ?? '—'}</td>
                                  <td className="muted">{dt(log.createdAt)}</td>
                                </tr>
                              );
                            })}
                            {!(data.auditLogs ?? []).length && (
                              <tr>
                                <td colSpan={4} className="muted" style={{ textAlign: 'center', padding: 20 }}>
                                  Записей аудита нет
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Confirm Dialogs */}
            <ConfirmDialog
              open={confirmBalanceOpen}
              title={`Корректировка баланса ${currency}`}
              description={`Изменение баланса пользователя на ${deltaAmount} ${currency}. Операция записывается в неизменяемый леджер и аудит.`}
              confirmLabel="Применить изменение"
              stepUpAction="balance.admin_adjust"
              onCancel={() => setConfirmBalanceOpen(false)}
              onConfirm={handleConfirmBalance}
            />

            <ConfirmDialog
              open={confirmBanOpen}
              title={`Блокировка пользователя ${dn.primary}`}
              description="Заблокированный пользователь увидит экран блокировки в Mini App и сможет подать апелляцию через /appeal."
              confirmLabel="Заблокировать"
              danger
              onCancel={() => setConfirmBanOpen(false)}
              onConfirm={handleConfirmBan}
            >
              <label className="muted" style={{ display: 'block', fontSize: 12, marginBottom: 6 }}>
                Срок блокировки в днях (оставьте пустым для бессрочной блокировки):
              </label>
              <input
                style={{ width: '100%' }}
                placeholder="Например: 7 (или пусто для бессрочного бана)"
                value={banDurationDays}
                onChange={(e) => setBanDurationDays(e.target.value)}
              />
            </ConfirmDialog>

            <ConfirmDialog
              open={Boolean(pendingRoles)}
              title="Изменение RBAC ролей оператора"
              description={`Новые роли: ${(pendingRoles ?? []).join(', ') || 'нет ролей (обычный пользователь)'}`}
              confirmLabel="Сохранить роли"
              stepUpAction="user.role.set"
              onCancel={() => setPendingRoles(null)}
              onConfirm={handleConfirmRoles}
            />
          </>
        )}
      </div>
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(drawerBody, document.body) : drawerBody;
  // END_BLOCK_USER_360_DRAWER
}
